import { useEffect, useMemo, useState, type CSSProperties } from "react";
import QRCode from "qrcode";
import { api } from "../api/client";
import { QrAttachedList } from "./QrAttachedList";

/** link — для QR, chatLink — для отправки в чат; с 07.10 обе ведут в чат бота (через «Старт» и галочку). */
type QrPart = { link: string; chatLink?: string; expiresAt: string };
/** 19.09 (владелец): допуск один на всех — капитана оператор отмечает тегом в отчёте. */
type GameQrResponse = { qr: QrPart & { used: number; limit: number } };

type Props = {
  bookingId: number | string;
  onClose: () => void;
};

const panel: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  background: "rgba(0,0,0,.68)",
  padding: "18px",
};

const sheet: CSSProperties = {
  width: "min(720px, 100%)",
  maxHeight: "85vh",
  overflow: "auto",
  border: "1px solid var(--surface-3, rgba(255,255,255,.22))",
  borderRadius: 18,
  background: "linear-gradient(180deg, var(--surface-1, #141923), var(--bg, #07090f))",
  color: "var(--text-1, #f4f0e8)",
  boxShadow: "0 -18px 60px rgba(0,0,0,.55)",
  padding: 16,
};

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
};

const card: CSSProperties = {
  border: "1px solid var(--surface-3, rgba(255,255,255,.18))",
  borderRadius: 14,
  background: "rgba(255,255,255,.035)",
  padding: 12,
};

const btn: CSSProperties = {
  height: 34,
  padding: "0 12px",
  borderRadius: 9,
  border: "1px solid var(--cyan, #2de2e6)",
  background: "rgba(45,226,230,.1)",
  color: "var(--text-1, #fff)",
  fontWeight: 700,
  cursor: "pointer",
};

function fmtLeft(expiresAt: string, now: number): string {
  const ms = Math.max(0, Date.parse(expiresAt.replace(" ", "T") + "Z") - now);
  if (ms <= 0) return "истёк";
  /* 19.09: ссылка тиммейтов живёт 14 дней — «20160:00» не читается, показываем дни и часы */
  if (ms >= 36e5) {
    const h = Math.floor(ms / 36e5);
    return h >= 24 ? `${Math.round(h / 24)} дн.` : `${h} ч.`;
  }
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function QrBlock({ title, part, note }: { title: string; part: QrPart; note?: string }) {
  const [src, setSrc] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(part.link, { width: 280, margin: 1, errorCorrectionLevel: "H" })
      .then((value) => { if (!cancelled) setSrc(value); })
      .catch(() => { if (!cancelled) setSrc(""); });
    return () => { cancelled = true; };
  }, [part.link]);
  return (
    <section style={card}>
      <h3 style={{ margin: "0 0 8px", fontSize: 15, textTransform: "uppercase", letterSpacing: ".08em" }}>{title}</h3>
      {src ? <img src={src} alt={`QR ${title}`} style={{ width: "100%", maxWidth: 280, display: "block", margin: "0 auto 10px", borderRadius: 10 }} /> : <div style={{ height: 220, display: "grid", placeItems: "center" }}>Генерация QR…</div>}
      {note ? <p style={{ margin: "0 0 8px", color: "var(--orange, #ffa033)", fontSize: 12 }}>{note}</p> : null}
      <code style={{ display: "block", wordBreak: "break-all", color: "var(--text-2, #c8c2b7)", fontSize: 11 }}>{part.chatLink ?? part.link}</code>
      <button
        type="button"
        style={{ ...btn, marginTop: 10 }}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(part.chatLink ?? part.link).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          }).catch(() => setCopied(false));
        }}
      >
        {copied ? "скопировано" : "копировать"}
      </button>
    </section>
  );
}

export function GameQrPanel({ bookingId, onClose }: Props) {
  const [data, setData] = useState<GameQrResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = () => {
    setLoading(true);
    setError("");
    api<GameQrResponse>(`/api/games/${encodeURIComponent(String(bookingId))}/qr`, { method: "POST", retries: 0 })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Не удалось создать QR"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [bookingId]);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const expired = useMemo(() => !!data && fmtLeft(data.qr.expiresAt, now) === "истёк", [data, now]);

  /* 16.09 (Влад): панель висит на экране всю игру — допуск обновляем сами за 15 минут до
     конца (сервер выдаёт новый, старый дорабатывает), а не ждём, пока игрок отсканирует «истёк». */
  const [renewedAt, setRenewedAt] = useState(0);
  useEffect(() => {
    if (!data || loading) return;
    const soonest = Date.parse(data.qr.expiresAt.replace(" ", "T") + "Z");
    if (!Number.isFinite(soonest) || soonest - now > 15 * 60000 || now - renewedAt < 60000) return;
    setRenewedAt(now);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, now, loading, renewedAt]);

  return (
    <div style={panel} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "var(--text-3, #8e8a82)", textTransform: "uppercase", letterSpacing: ".12em" }}>QR-привязка игры</div>
            <h2 style={{ margin: 0, fontSize: 22 }}>Бронь #{bookingId}</h2>
          </div>
          <button type="button" style={btn} onClick={load} disabled={loading}>{expired ? "сгенерировать заново" : "обновить"}</button>
          <button type="button" style={{ ...btn, borderColor: "var(--surface-3, #555)", background: "transparent" }} onClick={onClose}>×</button>
        </div>
        {loading ? <p>Готовлю QR…</p> : null}
        {error ? <p style={{ color: "var(--red, #ff4d5e)" }}>{error}</p> : null}
        {data ? (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, color: "var(--text-2, #c8c2b7)", fontSize: 12 }}>
              <span>В игре: {data.qr.used} из {data.qr.limit} · ссылка действует ещё {fmtLeft(data.qr.expiresAt, now)}</span>
            </div>
            <p style={{ margin: "0 0 10px", color: "var(--text-2, #c8c2b7)", fontSize: 12 }}>
              Один допуск на всех — и капитану, и остальным. Игрок сканирует QR камерой Telegram или из приложения
              Юнифанс (кнопка «Допуск»); скопированная ссылка ведёт в чат бота — он пришлёт кнопку «Открыть игру»,
              так допуск не теряется. Капитана отмечаете звездой в отчёте, отдельный QR для этого не нужен.
            </p>
            <div style={grid}>
              <QrBlock title="Допуск в игру" part={data.qr} />
            </div>
            <QrAttachedList bookingId={bookingId} now={now} />
          </>
        ) : null}
      </div>
    </div>
  );
}
